import { useState } from "react";
import { PDFDocument, degrees } from "pdf-lib";
import { renderPdfThumbnails, downloadBlob } from "../Helper/pdfThumpnails";
import Text from "./Text";
import Button from "./Buttons/Button";

const normalizeAngle = (angle) => ((angle % 360) + 360) % 360;

export default function PDFPageRotate() {
    const [fileName, setFileName] = useState("");
    const [pdfBytes, setPdfBytes] = useState(null);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setError("");
        setLoading(true);
        try {
            const buffer = await file.arrayBuffer();
            const bytes = new Uint8Array(buffer);
            const srcDoc = await PDFDocument.load(bytes);
            const baseRotations = srcDoc.getPages().map((p) => p.getRotation().angle);
            const thumbs = await renderPdfThumbnails(file, 0.35);

            setFileName(file.name);
            setPdfBytes(bytes);
            setPages(
                thumbs.map((dataUrl, i) => ({
                    id: `${i}-${Date.now()}`,
                    originalIndex: i,
                    dataUrl,
                    baseRotation: baseRotations[i],
                    rotationDelta: 0,
                }))
            );
        } catch (err) {
            console.error(err);
            setError("PDF konnte nicht gelesen werden.");
        } finally {
            setLoading(false);
        }
    };

    const rotatePage = (index, deltaDeg) => {
        setPages((prev) => {
            const copy = [...prev];
            copy[index] = {
                ...copy[index],
                rotationDelta: normalizeAngle(copy[index].rotationDelta + deltaDeg),
            };
            return copy;
        });
    };

    const rotateAll = (deltaDeg) => {
        setPages((prev) =>
            prev.map((p) => ({ ...p, rotationDelta: normalizeAngle(p.rotationDelta + deltaDeg) }))
        );
    };

    const resetRotations = () => {
        setPages((prev) => prev.map((p) => ({ ...p, rotationDelta: 0 })));
    };

    const savePdf = async () => {
        if (!pdfBytes || pages.length === 0) return;
        setSaving(true);
        setError("");
        try {
            const srcDoc = await PDFDocument.load(pdfBytes);
            const newDoc = await PDFDocument.create();

            for (const p of pages) {
                const [copied] = await newDoc.copyPages(srcDoc, [p.originalIndex]);
                copied.setRotation(degrees(normalizeAngle(p.baseRotation + p.rotationDelta)));
                newDoc.addPage(copied);
            }

            const outBytes = await newDoc.save();
            downloadBlob(
                outBytes,
                fileName.replace(/\.pdf$/i, "") + "-gedreht.pdf",
                "application/pdf"
            );
        } catch (err) {
            console.error(err);
            setError("Fehler beim Erstellen der PDF.");
        } finally {
            setSaving(false);
        }
    };

    const reset = () => {
        setFileName("");
        setPdfBytes(null);
        setPages([]);
        setError("");
    };

    const anyRotated = pages.some((p) => p.rotationDelta !== 0);

    return (
        <div className="flex flex-col gap-4">
            <Text type="body">
                PDF auswählen, um einzelne Seiten oder alle Seiten gemeinsam zu drehen.
            </Text>

            {!fileName && (
                <input type="file" accept="application/pdf" onChange={handleFileChange} />
            )}

            {loading && <Text type="body">Seiten werden geladen...</Text>}

            {fileName && !loading && (
                <div className="flex items-center justify-between">
                    <Text type="body" className="text-gray-500">{fileName} · {pages.length} Seiten</Text>
                    <Button buttonColor="bg-gray-200" onClickAction={reset}>
                        Andere Datei wählen
                    </Button>
                </div>
            )}

            {pages.length > 0 && (
                <>
                    <div className="flex flex-wrap gap-2">
                        <Button buttonColor="bg-amber-100" onClickAction={() => rotateAll(-90)}>
                            Alle ⟲ drehen
                        </Button>
                        <Button buttonColor="bg-amber-100" onClickAction={() => rotateAll(90)}>
                            Alle ⟳ drehen
                        </Button>
                        <Button buttonColor="bg-gray-200" onClickAction={resetRotations} disabled={!anyRotated}>
                            Rotationen zurücksetzen
                        </Button>
                    </div>

                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                        {pages.map((p, index) => (
                            <div
                                key={p.id}
                                className="relative border rounded-md p-2 flex flex-col items-center gap-2 bg-white"
                            >
                                <div className="flex items-center justify-center h-32 w-full overflow-hidden border border-gray-100 rounded">
                                    <img
                                        src={p.dataUrl}
                                        alt={`Seite ${index + 1}`}
                                        style={{
                                            transform: `rotate(${p.rotationDelta}deg)`,
                                            maxHeight: p.rotationDelta % 180 === 0 ? "100%" : "80%",
                                            maxWidth: p.rotationDelta % 180 === 0 ? "100%" : "80%",
                                            transition: "transform 0.2s",
                                        }}
                                    />
                                </div>

                                <span className="text-xs text-gray-500">
                                    Seite {index + 1}{p.rotationDelta !== 0 ? ` · ${p.rotationDelta}°` : ""}
                                </span>

                                <div className="flex gap-1">
                                    <Button buttonColor="bg-blue-100" onClickAction={() => rotatePage(index, -90)}>
                                        ⟲
                                    </Button>
                                    <Button buttonColor="bg-blue-100" onClickAction={() => rotatePage(index, 90)}>
                                        ⟳
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}

            {error && <p style={{ color: "red" }}>{error}</p>}

            {pages.length > 0 && (
                <Button
                    buttonColor={saving ? "bg-gray-400" : "bg-green-100"}
                    onClickAction={savePdf}
                    disabled={saving}
                >
                    {saving ? "Wird erstellt..." : "Als PDF speichern"}
                </Button>
            )}
        </div>
    );
}